/**
 * Where the org stands (`Standing`: trial, active, past due, read-only, suspended) and how close it is to its plan's
 * limits: what the plan banner atop the space's pages says. The server is the source of truth (it refuses what the
 * org can't do); this only says so ahead of time.
 */
import type { SpaceOut, Standing } from "../api/types.gen.ts";

const DAY_MS = 86_400_000;
/** Trial days left from which its banner shows (before: the sidebar's quiet chip), and from which it's loudest. */
const SOON_DAYS = 3;
const LAST_DAY = 1;
/** Storage used, as a share of the plan's, from which its banner shows. */
const NEAR_FULL = 0.8;

/** Nothing in the org changes (its trial or plan ended), but reading, exporting, leaving… */
export function readOnly(space: Pick<SpaceOut, "standing">): boolean {
  const s = space.standing.state;
  return s === "grace" || s === "suspended";
}

/** Whole days until `iso` (0 once it's today or past). */
function daysUntil(iso: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(iso) - now) / DAY_MS));
}

/** A trial's whole days left until it ends at `endsAt` (0 on its last day, or once over). */
export const trialDaysLeft = (endsAt: string, now = Date.now()): number => daysUntil(endsAt, now);

/** How loud a banner is. */
type NoticeTone = "amber" | "red";

/** Something the plan banner says (`kind` names its message), how loudly, and its values. */
export interface PlanNotice {
  kind: "trial" | "pastDue" | "grace" | "suspended" | "storageNear" | "storageFull" | "emailsUsed";
  tone: NoticeTone;
  /** The trial's days left; storage used, in percent. */
  count?: number;
}

/**
 * Where the org stands, if it's news: to admins always (a trial only in its last days: before, the sidebar says how
 * many are left), to everyone once it's read-only.
 */
function stateNotice(st: Standing, admin: boolean, now: number): PlanNotice | null {
  switch (st.state) {
    case "trial": {
      if (!(admin && st.trial_ends_at)) {
        return null;
      }
      const count = daysUntil(st.trial_ends_at, now);
      if (count > SOON_DAYS) {
        return null;
      }
      return { kind: "trial", tone: count <= LAST_DAY ? "red" : "amber", count };
    }
    case "past_due":
      return admin ? { kind: "pastDue", tone: "amber" } : null;
    case "grace":
      return { kind: "grace", tone: "amber" };
    case "suspended":
      return { kind: "suspended", tone: "red" };
    default:
      return null;
  }
}

/** The limits it's near or at (what comes in is refused at them, so everyone hears; near them, admins). */
function limitNotices(st: Standing, admin: boolean): PlanNotice[] {
  const l = st.limits;
  if (!l || st.state === "suspended") {
    return [];
  }
  const out: PlanNotice[] = [];
  const used = st.usage.storage / l.storage;
  if (used >= 1) {
    out.push({ kind: "storageFull", tone: "red" });
  } else if (used >= NEAR_FULL && admin) {
    out.push({ kind: "storageNear", tone: "amber", count: Math.floor(used * 100) });
  }
  if (st.usage.emails_available <= 0) {
    out.push({ kind: "emailsUsed", tone: "amber" });
  }
  return out;
}

/** What the plan banner says, most important first. */
export function planNotices(st: Standing, admin: boolean, now = Date.now()): PlanNotice[] {
  const state = stateNotice(st, admin, now);
  return [...(state ? [state] : []), ...limitNotices(st, admin)];
}
